/***************************************************
 * src/TryIt.jsx
 ***************************************************/
import React, { useState } from "react"; // <-- Removed unused 'useEffect'
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import "./Survey.css";
import "./RegistrationForm.css";

function TryIt({ isDarkMode }) {
  const navigate = useNavigate();

  // 1) Keep your existing state
  const [surveyTitle, setSurveyTitle] = useState("");
  const [jobTitles, setJobTitles] = useState(["", "", "", "", ""]);
  const [scenarios, setScenarios] = useState(["", "", "", "", ""]);

  const [analysisResult, setAnalysisResult] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysisInProgress, setAnalysisInProgress] = useState(false);

  // 2) Determine theme string
  const recaptchaTheme = isDarkMode ? "dark" : "light";

  // 3) Old placeholders
  const jobTitlePlaceholders = [
    "Project Manager",
    "Data Analyst",
    "Marketing Director",
    "HR Supervisor",
    "Lead Developer",
  ];
  const scenarioPlaceholders = [
    "perspectives on new social content",
    "resistance to adopting new tech",
    "issues managing remote/hybrid setup",
    "outlook on company growth",
    "attitude toward new company policies",
  ];

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleJobTitleChange = (idx, val) => {
    const updated = [...jobTitles];
    updated[idx] = val;
    setJobTitles(updated);
  };

  const handleScenarioChange = (idx, val) => {
    const updated = [...scenarios];
    updated[idx] = val;
    setScenarios(updated);
  };

  // 4) Poll logic for "Run Simulation"
  const handleRunSimulation = async () => {
    setErrorMessage("");
    setAnalysisResult(null);

    if (!recaptchaToken) {
      setErrorMessage("Please complete the reCAPTCHA challenge.");
      return;
    }

    // Filter blanks
    const filteredJobs = jobTitles.map((t) => t.trim()).filter(Boolean);
    const filteredScen = scenarios.map((s) => s.trim()).filter(Boolean);

    if (filteredJobs.length < 3) {
      setErrorMessage("Please enter at least 3 job titles.");
      return;
    }
    if (filteredScen.length < 3) {
      setErrorMessage("Please enter at least 3 scenarios.");
      return;
    }

    setAnalysisInProgress(true);
    setLoading(true);

    try {
      const startResp = await fetch("/api/tryIt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          surveyTitle,
          jobTitles: filteredJobs,
          scenarios: filteredScen,
          token: recaptchaToken,
        }),
      });

      if (!startResp.ok) {
        const errData = await startResp.json();
        throw new Error(errData.message || "Error running simulation.");
      }

      const startData = await startResp.json();
      if (!startData.runId) {
        throw new Error("No runId returned from server.");
      }

      const runId = startData.runId;
      console.log("TryIt run started, runId =", runId);

      // Poll for final analysis
      const pollInterval = setInterval(async () => {
        try {
          const resp = await fetch(`/api/tryIt/${runId}`);
          if (!resp.ok) {
            clearInterval(pollInterval);
            setAnalysisInProgress(false);
            setLoading(false);

            const errData = await resp.json();
            setErrorMessage(errData.error || "Error fetching analysis");
            return;
          }

          const data = await resp.json();
          console.log("poll =>", data);

          // Check status
          if (data.status === "pending") {
            console.log("Aggregator still pending for runId", runId);
            return;
          } else if (data.status === "failed") {
            clearInterval(pollInterval);
            setAnalysisInProgress(false);
            setLoading(false);
            setErrorMessage(
              data.error || "Aggregator failed. Please try again later."
            );
            return;
          } else if (data.status === "complete") {
            clearInterval(pollInterval);
            setAnalysisInProgress(false);
            setLoading(false);
            setAnalysisResult(data);
            setShowModal(true);
          } else {
            clearInterval(pollInterval);
            setAnalysisInProgress(false);
            setLoading(false);
            setErrorMessage("Unknown status from server.");
          }
        } catch (pollErr) {
          console.error("Error polling aggregator:", pollErr);
          clearInterval(pollInterval);
          setAnalysisInProgress(false);
          setLoading(false);
          setErrorMessage(pollErr.message);
        }
      }, 3000);
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message);
      setAnalysisInProgress(false);
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleRunSimulation();
  };

  const closeModal = () => setShowModal(false);

  return (
    <div className="registration-form-container">
      {/* Overlay that blocks all clicks behind it */}
      {analysisInProgress && (
        <div className="analysis-overlay">
          <div className="analysis-overlay-content">
            <h2>Please wait</h2>
            <p>We are generating and analyzing your scenario... This may take a minute.</p>
            <div className="loading-bar"></div>
          </div>
        </div>
      )}

      <form className="registration-form" onSubmit={handleSubmit}>
        <h1>Try It - Anonysis Simulation</h1>
        <p style={{ marginBottom: "1rem" }}>
          Enter a Feedback Group title. Enter 3-5 unique job titles or roles of your respondents. 
          Enter 3-5 topics you would expect to see in their feedback reponses.
          We'll simulate their responses with your inputs, and then provide our AI analysis.
        </p>

        <h2>Feedback Group</h2>
        <input
          type="text"
          value={surveyTitle}
          onChange={(e) => setSurveyTitle(e.target.value)}
          placeholder="e.g. Team Collaboration"
          disabled={analysisInProgress}
        />

        <h2>Job Titles or Roles (3 to 5)</h2>
        {jobTitles.map((jt, idx) => (
          <div key={idx}>
            <label>Role #{idx + 1}</label>
            <input
              type="text"
              value={jt}
              onChange={(e) => handleJobTitleChange(idx, e.target.value)}
              placeholder={jobTitlePlaceholders[idx]}
              disabled={analysisInProgress}
            />
          </div>
        ))}

        <h2>Topics (3 to 5)</h2>
        {scenarios.map((sc, idx) => (
          <div key={idx}>
            <label>Topic #{idx + 1}</label>
            <input
              type="text"
              value={sc}
              onChange={(e) => handleScenarioChange(idx, e.target.value)}
              placeholder={scenarioPlaceholders[idx]}
              disabled={analysisInProgress}
            />
          </div>
        ))}

        {errorMessage && <p className="error-message">{errorMessage}</p>}

        <div className="recaptcha-container">
          <ReCAPTCHA
            key={isDarkMode ? "dark-recaptcha" : "light-recaptcha"}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={onRecaptchaChange}
            theme={recaptchaTheme}
          />
        </div>

        <button
          type="submit"
          className="submit-btn"
          disabled={analysisInProgress || loading}
        >
          {loading ? "Running..." : "Run Simulation"}
        </button>
      </form>

      {/* Modal for Analysis Results */}
      {showModal && analysisResult && analysisResult.status === "complete" && (
        <div className="modal-backdrop tryit-modal-backdrop">
          <div className="modal-content">
            <h2>Analysis Results</h2>
            <hr />
            <p><strong>Feedback Group Title:</strong> {analysisResult.surveyTitle}</p>
            <p><strong>Number of Responses:</strong> {analysisResult.responseCount}</p>

            <section style={{ marginTop: "1rem" }}>
              <h3>Key Bullet Points</h3>
              <ul>
                {analysisResult.bulletPoints?.map((bp, i) => (
                  <li key={i}>{bp}</li>
                ))}
              </ul>
            </section>

            <section style={{ marginTop: "1rem" }}>
              <h3>Actionable Insights</h3>
              <ul>
                {analysisResult.actionableInsights?.map((ai, i) => (
                  <li key={i}>{ai}</li>
                ))}
              </ul>
            </section>

            <section style={{ marginTop: "1rem" }}>
              <h3>Overall Summary</h3>
              <p>{analysisResult.summaryText}</p>
            </section>

            <section style={{ marginTop: "1rem" }}>
              <h3>Top Words</h3>
              {analysisResult.topWords &&
                analysisResult.topWords
                  .sort((a, b) => b.count - a.count)
                  .map((tw, i) => (
                    <div key={i}>
                      {tw.word}: {tw.count}
                    </div>
                  ))}
            </section>

            <div className="modal-actions">
              <button className="logout-btn" onClick={closeModal}>
                Close
              </button>
              <button className="logout-btn" onClick={() => navigate("/register")}>
                Register Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extra button at bottom */}
      <div style={{ marginTop: "2rem", textAlign: "center" }}>
        <button className="submit-btn" onClick={() => navigate("/register")}>
          Register Now
        </button>
      </div>
    </div>
  );
}

export default TryIt;
